import type { DefaultAutomationTemplateMetadata } from '@reach/shared-types/automation-template';
import type { ApiResponse } from '../../../lib/api';
import { API_ENDPOINTS } from '../../../lib/constants';
import { apiRequest } from '../../../lib/api-utils';
import type {
  CreateAutomationTemplateParams,
  DuplicateDefaultsResponse,
  TemplateResponse,
  TemplatesResponse,
  UpdateAutomationTemplateParams,
} from './templates.types';

export async function getAutomationTemplates(): Promise<TemplatesResponse> {
  const response = await apiRequest<ApiResponse<TemplatesResponse>>(
    API_ENDPOINTS.AUTOMATION_TEMPLATES.LIST,
  );
  return response.data;
}

export async function getAutomationTemplate(
  id: string,
): Promise<TemplateResponse> {
  const response = await apiRequest<ApiResponse<TemplateResponse>>(
    API_ENDPOINTS.AUTOMATION_TEMPLATES.GET(id),
  );
  return response.data;
}

export async function createAutomationTemplate(
  params: CreateAutomationTemplateParams,
): Promise<TemplateResponse> {
  const response = await apiRequest<ApiResponse<TemplateResponse>>(
    API_ENDPOINTS.AUTOMATION_TEMPLATES.CREATE,
    {
      method: 'POST',
      body: JSON.stringify(params),
    },
  );
  return response.data;
}

export async function updateAutomationTemplate(
  id: string,
  params: UpdateAutomationTemplateParams,
): Promise<TemplateResponse> {
  const response = await apiRequest<ApiResponse<TemplateResponse>>(
    API_ENDPOINTS.AUTOMATION_TEMPLATES.UPDATE(id),
    {
      method: 'PUT',
      body: JSON.stringify(params),
    },
  );
  return response.data;
}

export async function duplicateDefaultTemplates(): Promise<DuplicateDefaultsResponse> {
  const response = await apiRequest<ApiResponse<DuplicateDefaultsResponse>>(
    API_ENDPOINTS.AUTOMATION_TEMPLATES.DUPLICATE_DEFAULTS,
    {
      method: 'POST',
    },
  );
  return response.data;
}

export async function getDefaultAutomationMetadata(): Promise<{
  metadata: DefaultAutomationTemplateMetadata[];
}> {
  const response = await apiRequest<
    ApiResponse<{ metadata: DefaultAutomationTemplateMetadata[] }>
  >(API_ENDPOINTS.AUTOMATION_TEMPLATES.DEFAULT_METADATA);
  return response.data;
}

export async function createPartnerCopyOfDefaultAutomation(
  templateName: string,
): Promise<{ success: boolean; created: boolean; templateId: string | null }> {
  const response = await apiRequest<
    ApiResponse<{
      success: boolean;
      created: boolean;
      templateId: string | null;
    }>
  >(API_ENDPOINTS.AUTOMATION_TEMPLATES.COPY_DEFAULT_AUTOMATION, {
    method: 'POST',
    body: JSON.stringify({ templateName }),
  });
  return response.data;
}

export async function createPartnerAutomationFromDefault(params: {
  defaultTemplateName: string;
  customName: string;
  customDescription: string;
}): Promise<{ created: boolean; templateId: string | null }> {
  const response = await apiRequest<
    ApiResponse<{
      created: boolean;
      templateId: string | null;
    }>
  >(API_ENDPOINTS.AUTOMATION_TEMPLATES.CREATE_FROM_DEFAULT, {
    method: 'POST',
    body: JSON.stringify(params),
  });
  return response.data;
}

export async function syncAutomationTemplates(): Promise<void> {
  await apiRequest<ApiResponse<Record<string, never>>>(
    API_ENDPOINTS.AUTOMATION_TEMPLATES.SYNC,
    {
      method: 'POST',
    },
  );
}

export async function checkActiveSyncJobs(): Promise<{
  hasActiveJobs: boolean;
}> {
  const response = await apiRequest<
    ApiResponse<{
      hasActiveJobs: boolean;
    }>
  >(API_ENDPOINTS.AUTOMATION_TEMPLATES.SYNC_STATUS);
  return response.data;
}
